import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { apiClient } from '../../lib/api-client';
import Editor from 'react-simple-code-editor';
import Prism from 'prismjs';
import 'prismjs/components/prism-yaml';
import 'prismjs/themes/prism-tomorrow.css';
import yaml from 'js-yaml';

interface AddTenantDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function AddTenantDialog({ open, onClose, onSuccess }: AddTenantDialogProps) {
  const [tenantCode, setTenantCode] = useState('');
  const [yamlContent, setYamlContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Load template when dialog opens
  useEffect(() => {
    if (open && !yamlContent) {
      loadTemplate();
    }
  }, [open]);

  const loadTemplate = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.getTenantTemplateYaml();
      setYamlContent(response.yaml);
    } catch (err: any) {
      console.warn('Failed to load tenant template from API, using built-in fallback:', err);
      // Fallback to basic template (API may be missing tenant-template.yaml in Postgres)
      setYamlContent(`tenant:
  name: newtenant
  display_name: "New Tenant"

cloud_providers:
  aws:
    tenant_name: newtenant
    enabled_regions:
      - eu-west-1
      - eu-west-2
`);
    } finally {
      setLoading(false);
    }
  };

  const validateYaml = (content: string): boolean => {
    try {
      yaml.load(content);
      setValidationError(null);
      return true;
    } catch (err: any) {
      setValidationError(`Invalid YAML: ${err.message}`);
      return false;
    }
  };

  const handleSave = async () => {
    if (!tenantCode.trim()) {
      setError('Tenant code is required');
      return;
    }

    if (!validateYaml(yamlContent)) {
      return;
    }

    setSaving(true);
    setError(null);
    
    try {
      await apiClient.createTenantYaml(tenantCode.toLowerCase(), yamlContent);
      onSuccess();
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create tenant');
    } finally {
      setSaving(false);
    }
  };

  const handleYamlChange = (newValue: string) => {
    setYamlContent(newValue);
    setValidationError(null);
  };

  const highlightYaml = (code: string) => {
    return Prism.highlight(code, Prism.languages.yaml, 'yaml');
  };

  const handleClose = () => {
    if (!saving) {
      setTenantCode('');
      setYamlContent('');
      setError(null);
      setValidationError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent maxWidth="4xl">
        <DialogHeader>
          <DialogTitle>Add New Tenant</DialogTitle>
          <DialogDescription>
            Create a new tenant with YAML configuration
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <div className="space-y-4">
          <div>
            <label htmlFor="tenantCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Tenant Code (lowercase, e.g., "acme")
            </label>
            <input
              id="tenantCode"
              type="text"
              value={tenantCode}
              onChange={(e) => setTenantCode(e.target.value.toLowerCase())}
              placeholder="Enter tenant code"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 outline-none"
              disabled={saving || loading}
            />
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-800 dark:text-red-200">
              {error}
            </div>
          )}

          {validationError && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 text-amber-800 dark:text-amber-200">
              {validationError}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Tenant Configuration (YAML)
            </label>
            {loading ? (
              <div className="flex justify-center py-8 bg-gray-900 rounded-lg">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <div className="relative">
                <div className="bg-gray-900 rounded-lg overflow-hidden border-2 border-gray-700 focus-within:border-blue-500">
                  <div className="max-h-[500px] overflow-auto custom-scrollbar">
                    <Editor
                      value={yamlContent}
                      onValueChange={handleYamlChange}
                      highlight={highlightYaml}
                      padding={16}
                      style={{
                        fontFamily: '"Fira Code", "Fira Mono", monospace',
                        fontSize: 14,
                        backgroundColor: '#1e293b',
                        color: '#e2e8f0',
                        minHeight: '350px',
                      }}
                      className="outline-none"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={saving || loading || !tenantCode.trim() || !!validationError}>
            {saving ? 'Creating...' : 'Create Tenant'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

